import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Share2, Calendar, Flame } from "lucide-react";
import { useState } from "react";
import { ShareSheet } from "@/components/share/ShareSheet";
import { Link } from "react-router-dom";
import { useUnifiedStats } from "@/hooks/useUnifiedStats";

interface ResultModalProps {
  isOpen: boolean;
  result: "win" | "lose";
  solution: string;
  guesses: string[];
  results: Array<Array<"correct" | "present" | "absent">>;
  onPlayAgain: () => void;
  onClose?: () => void;
  isAlreadyPlayed?: boolean;
  puzzleNumber?: number;
  onShowInstallPrompt?: () => void;
  hintsUsed?: number;
}

export function ResultModal({ 
  isOpen, 
  result, 
  solution, 
  guesses, 
  results, 
  onPlayAgain, 
  onClose, 
  isAlreadyPlayed = false, 
  puzzleNumber,
  hintsUsed = 0
}: ResultModalProps) {
  const [showShareSheet, setShowShareSheet] = useState(false);
  const { currentStreak } = useUnifiedStats();

  // Playful microcopy based on result
  const getWinMessage = () => {
    const attempts = guesses.length;
    if (attempts === 1) return "Genius! 🧠";
    if (attempts === 2) return "Brilliant! ✨";
    if (attempts === 3) return "Impressive! 🎯";
    if (attempts === 4) return "Well done! 👏";
    return "Phew! 😅";
  };

  const getLoseMessage = () => {
    const messages = [
      "So close! 💪",
      "You'll get it next time! 🌟",
      "Good effort! 🎲",
      "That was a tough one! 🧩",
    ];
    return messages[Math.floor(Math.random() * messages.length)];
  };

  const isWin = result === "win";

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent 
          className="sm:max-w-sm border-0 bg-background/95 backdrop-blur-sm" 
          aria-describedby="result-description"
        >
          <DialogTitle className="sr-only">
            {isWin ? "Congratulations! You won!" : "Game Over"}
          </DialogTitle>
          <div className="text-center space-y-6 py-4">
            {/* Playful message */}
            <p 
              className={`text-lg opacity-0 animate-[fadeInUp_0.4s_ease-out_forwards] ${isWin ? 'text-muted-foreground' : 'text-foreground/80'}`}
            >
              {isWin ? getWinMessage() : getLoseMessage()}
            </p>

            {/* Solution word with glow effect */}
            <div 
              className="relative opacity-0 animate-[fadeInUp_0.4s_ease-out_forwards]"
              style={{ animationDelay: '100ms' }}
            >
              <div 
                className={`absolute inset-0 blur-2xl ${isWin ? 'opacity-30' : 'opacity-20'}`}
                style={{
                  background: isWin 
                    ? "linear-gradient(135deg, hsl(var(--tile-correct)) 0%, hsl(var(--primary)) 100%)"
                    : "linear-gradient(135deg, hsl(var(--tile-present)) 0%, hsl(var(--accent)) 100%)"
                }}
              />
              <h2 className="relative text-4xl font-bold tracking-[0.2em] uppercase">
                {solution}
              </h2>
            </div>

            {/* Simple stat line */}
            <p 
              id="result-description" 
              className="text-sm text-muted-foreground opacity-0 animate-[fadeInUp_0.4s_ease-out_forwards]"
              style={{ animationDelay: '200ms' }}
            >
              {isWin 
                ? `Solved in ${guesses.length} guess${guesses.length === 1 ? "" : "es"}${hintsUsed > 0 ? ` (${hintsUsed} hint${hintsUsed === 1 ? "" : "s"} used)` : ""}`
                : "Now you know for next time!"
              }
            </p>

            {/* Streak indicator */}
            {isWin && currentStreak >= 2 ? (
              <div 
                className="flex items-center justify-center gap-1.5 text-orange-500 opacity-0 animate-[fadeInUp_0.4s_ease-out_forwards]"
                style={{ animationDelay: '300ms' }}
              >
                <Flame className="h-5 w-5 fill-orange-500" />
                <span className="font-semibold">{currentStreak} day streak!</span>
              </div>
            ) : !isWin ? (
              <p 
                className="text-sm text-muted-foreground/70 opacity-0 animate-[fadeInUp_0.4s_ease-out_forwards]"
                style={{ animationDelay: '300ms' }}
              >
                Streak ended — start fresh tomorrow!
              </p>
            ) : null}

            {/* Primary action: Share */}
            <Button 
              onClick={() => setShowShareSheet(true)}
              size="lg"
              className={`w-full gap-2 h-12 text-base font-semibold opacity-0 animate-[fadeInUp_0.4s_ease-out_forwards] ${
                isWin 
                  ? '' 
                  : 'bg-secondary hover:bg-secondary/80 text-secondary-foreground'
              }`}
              style={{ animationDelay: '400ms' }}
              variant={isWin ? "default" : "secondary"}
            >
              <Share2 className="h-5 w-5" />
              {isWin ? "Share My Win" : "Share Result"}
            </Button>

            {/* Secondary actions */}
            <div 
              className="flex flex-col gap-2 pt-2 opacity-0 animate-[fadeInUp_0.4s_ease-out_forwards]"
              style={{ animationDelay: '500ms' }}
            >
              {!isAlreadyPlayed ? (
                <Button 
                  onClick={onPlayAgain} 
                  variant="ghost" 
                  size="sm"
                  className="text-muted-foreground hover:text-foreground"
                >
                  Play Again
                </Button>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Come back tomorrow for a new puzzle
                </p>
              )}
              
              <Link to="/archive">
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-muted-foreground hover:text-foreground gap-1"
                >
                  <Calendar className="h-4 w-4" />
                  Explore Archive
                </Button>
              </Link>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Sheet */}
      <ShareSheet 
        isOpen={showShareSheet}
        onClose={() => setShowShareSheet(false)}
        shareOptions={{
          puzzleNumber: puzzleNumber || 1,
          attempts: guesses.length,
          won: result === "win",
          results,
        }}
      />
    </>
  );
}
